<template>
    <Teleport to="body">
        <div
            v-if="visible"
            class="hts-drawer"
            :class="{ visible: drawerVisible, 'drawer-right': right }">
            <div
                class="hts-drawer__overlay"
                @click="handleCloseClick" />
            <div
                class="hts-drawer__container"
                :style="cStyle">
                <div class="hts-drawer__title">
                    <div>
                        <h3>
                            {{ title }}
                        </h3>
                        <p v-if="describe">
                            {{ describe }}
                        </p>
                    </div>
                    <hi-x-mark-icon
                        v-if="!closeHidden"
                        class="close-button w-7 h-7"
                        aria-hidden="true"
                        @click="handleCloseClick" />
                </div>
                <hts-divider />
                <template v-if="visible">
                    <template v-if="submit !== undefined">
                        <hts-form
                            ref="drawerForm"
                            :rules="rules"
                            class="flex flex-col flex-1 max-w-full overflow-hidden"
                            @submit="handleSubmit">
                            <div class="hts-drawer__body">
                                <hts-loading
                                    :loading="loading"
                                    class="hts-drawer__body">
                                    <slot />
                                </hts-loading>
                            </div>
                            <div>
                                <hts-loading :loading="loading">
                                    <div class="hts-drawer__footer">
                                        <slot name="footer">
                                            <div class="flex flex-row gap-5">
                                                <hts-form-button
                                                    v-if="submit !== undefined"
                                                    icon-name="hi-check-icon"
                                                    icon-position="right"
                                                    color="primary"
                                                    type="submit"
                                                    :loading="submitting"
                                                    :disabled="disabled">
                                                    {{ $t('labels.save') }}
                                                </hts-form-button>

                                                <hts-form-button
                                                    icon-name="hi-x-mark-icon"
                                                    icon-position="right"
                                                    color="error"
                                                    @click="handleCloseClick">
                                                    {{ $t('labels.close') }}
                                                </hts-form-button>
                                            </div>

                                            <hts-form-button
                                                v-if="resetEnable"
                                                type="text"
                                                color="error"
                                                icon-name="hi-arrow-path-icon"
                                                icon-position="right"
                                                :loading="resetting"
                                                @click="handleResetClick">
                                                {{ $t('labels.reset') }}
                                            </hts-form-button>
                                        </slot>
                                    </div>
                                </hts-loading>
                            </div>
                        </hts-form>
                    </template>
                    <template v-else>
                        <hts-loading
                            :loading="loading"
                            class="hts-drawer__body flex-1">
                            <slot />
                        </hts-loading>
                    </template>
                </template>
            </div>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import screen from '~/components/utils/screen'
const slots = defineSlots()

const hasFooterSlot = computed(() => !!slots.footer)

const breakpoint = screen()

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  title: String,
  describe: String,
  isClose: {
    type: Boolean,
    default: false
  },
  right: {
    type: Boolean,
    default: true
  },
  size: [String, Number],
  submit: Function,
  reset: Function,
  rules: {
    type: Object,
    default: () => {}
  },
  closeHidden: Boolean,
  noReset: Boolean,
  disabled: Boolean,
  loading: Boolean
})
const emits = defineEmits(['update:modelValue', 'submit', 'close', 'reset', 'handleSubmitClose', 'visible', 'show', 'hide'])
const { modelValue, size, right, submit, reset, rules, isClose, noReset, loading } = toRefs(props)
const submitting = ref<boolean>(false)
const resetting = ref<boolean>(false)
const visible = computed({
  get () {
    return modelValue.value
  },
  set (val: boolean) {
    emits('update:modelValue', val)
  }
})
const drawerVisible = ref(false)
const drawerForm = ref<any>(null)

const cStyle = computed(() => {
  if (breakpoint.value.isMobile) {
    return {
      width: '100%'
    }
  }

  if (size && size.value) {
    if (typeof size.value === 'number') {
      return {
        width: size.value + '%'
      }
    }
    if (typeof size.value === 'string') {
      switch (size.value) {
      case 'small':
        return { width: '20%' }
      case 'medium':
        return { width: '50%' }
      case 'large':
        return { width: '70%' }
      case 'xl':
        return { width: '85%' }
      case 'full':
        return { width: '100%' }
      default:
        return { width: size.value }
      }
    }
  }
  return {
    width: '40%'
  }
})

const resetEnable = computed(() => !!(reset && reset.value && typeof reset.value === 'function'))

watch(
  () => visible.value,
  (val: boolean) => {
    if (val) {
      show()
    } else {
      hide()
    }
  }
)

const show = () => {
  nextTick(() => {
    drawerVisible.value = true
    emits('visible', true)
    setTimeout(() => {
      emits('show')
    }, 300)
  })
}

const hide = () => {
  nextTick(() => {
    drawerVisible.value = false
    emits('visible', false)
    emits('hide')
  })
}
const submitForm = (values: any, form: any) => {
  emits('submit')

  if (submit && typeof submit.value === 'function') {
    // || !hasFooterSlot.value
    if (!submitting.value) {
      submitting.value = true
      if (submit.value.constructor.name === 'AsyncFunction') {
        Promise.resolve(submit.value())
          .then(() => {
            if (isClose.value) {
              visible.value = false
              emits('handleSubmitClose')
            }
            if (!noReset.value) {
              // resetForm && resetForm()
            }
            nextTick(() => {
              submitting.value = false
            })
          })
          .catch(() => {
            nextTick(() => {
              submitting.value = false
            })
          })
          .finally(() => {
            nextTick(() => {
              submitting.value = false
            })
          })
      } else {
        submit.value()
        nextTick(() => {
          submitting.value = false
        })
      }
    }
  }
}

const handleSubmit = $lodash.debounce(submitForm, 500)

const handleCloseClick = (e: any) => {
  e.preventDefault()
  e.stopPropagation()
  visible.value = false
  emits('close', e)
}
const handleResetClick = $lodash.debounce((e: any) => {
  emits('reset', e)
  if (reset && typeof reset.value === 'function') {
    if (!resetting.value) {
      resetting.value = true
      if (reset.value.constructor.name === 'AsyncFunction') {
        Promise.resolve(reset.value())
          .then(() => {
            resetting.value = false
          })
          .catch(() => {
            resetting.value = false
          })
          .finally(() => {
            resetting.value = false
          })
      } else {
        reset.value()
        resetting.value = false
      }
    }
  }
}, 300)
</script>
